import {
  StandardsVariant,
  TrackAssessmentCategory,
  TrackAssessmentDimCriteria,
  TrackAssessmentStandard,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

const STRINGS = appStrings.modules.benchmarks;

const ACADEMIC_SLUG = 'academic-foundations';

type Tenure = TrackAssessmentStandard['tenure'];

const TENURE_LABELS: Record<Tenure, string> = {
  'High School': STRINGS.highSchool,
  'Post High School': STRINGS.college,
};

// High school grades carry their number as the value ('9'), college years a phrase.
const gradeTenure = (gradeValue: string): Tenure =>
  /^\d+$/.test(gradeValue) ? 'High School' : 'Post High School';

export type BenchmarkYear = Readonly<{
  id: number;
  label: string;
  tenure: Tenure;
}>;

export type BenchmarkRubric = Readonly<{
  key: string;
  category: TrackAssessmentCategory;
  variant: StandardsVariant;
  tenure: Tenure;
  /** Absent for track standards, which span a whole tenure. */
  year?: BenchmarkYear;
}>;

/**
 * Every Are You on Track rubric: the academic standards by track for each tenure, then each
 * other category's criteria by level, grade by grade.
 */
export const buildBenchmarkRubrics = (
  standards: readonly TrackAssessmentStandard[],
  criteria: readonly TrackAssessmentDimCriteria[],
): BenchmarkRubric[] => {
  const tenures = Object.keys(TENURE_LABELS) as Tenure[];

  const byTrack = tenures.flatMap((tenure): BenchmarkRubric[] => {
    const tenureStandards = standards.filter(
      (s) => s.category.slug === ACADEMIC_SLUG && s.tenure === tenure,
    );
    const category = tenureStandards[0]?.category;

    if (!category) return [];

    return [
      {
        key: `${ACADEMIC_SLUG}-${tenure}`,
        category,
        tenure,
        variant: {
          kind: 'by-track',
          standards: tenureStandards,
          tenureLabel: TENURE_LABELS[tenure],
        },
      },
    ];
  });

  const byLevel = new Map<string, BenchmarkRubric>();

  const sortedCriteria = [...criteria].sort(
    (a, b) =>
      a.academic_grade_id - b.academic_grade_id ||
      (a.dimension?.category.id ?? 0) - (b.dimension?.category.id ?? 0),
  );

  for (const row of sortedCriteria) {
    const category = row.dimension?.category;

    if (!category || category.slug === ACADEMIC_SLUG || !row.academicGrade) continue;

    const key = `${category.slug}-${row.academic_grade_id}`;
    const rubric = byLevel.get(key);

    if (rubric?.variant.kind === 'by-level') rubric.variant.criteria.push(row);
    else {
      const year: BenchmarkYear = {
        id: row.academicGrade.id,
        label: row.academicGrade.name,
        tenure: gradeTenure(row.academicGrade.value),
      };

      byLevel.set(key, {
        key,
        category,
        tenure: year.tenure,
        year,
        variant: { kind: 'by-level', criteria: [row], gradeLabel: year.label },
      });
    }
  }

  return [...byTrack, ...Array.from(byLevel.values())];
};

const uniqueBy = <T, K>(items: readonly T[], key: (item: T) => K): T[] =>
  Array.from(new Map(items.map((item) => [key(item), item])).values());

/** The unlocks and years a rubric can be picked by, in rubric order. */
export const benchmarkOptions = (rubrics: readonly BenchmarkRubric[]) => ({
  categories: uniqueBy(rubrics, (rubric) => rubric.category.id).map((rubric) => rubric.category),
  years: uniqueBy(
    rubrics.flatMap((rubric) => (rubric.year ? [rubric.year] : [])),
    (year) => year.id,
  ),
});

/** Track standards cover a whole tenure, so any year in that tenure finds them. */
export const findBenchmarkRubric = (
  rubrics: readonly BenchmarkRubric[],
  categoryId: number,
  year: BenchmarkYear,
): BenchmarkRubric | undefined =>
  rubrics.find(
    (rubric) =>
      rubric.category.id === categoryId &&
      (rubric.year ? rubric.year.id === year.id : rubric.tenure === year.tenure),
  );
